import { useMemo } from 'react';
import type { Edge } from '@xyflow/react';
import type { OntologyFlowNode } from '@/utils/ontologyTree/types';

const X_GAP = 360;
const Y_GAP_DISEASE = 100;
const Y_GAP_GENE = 58;

export function useTreeLayout(nodes: OntologyFlowNode[], edges: Edge[]): OntologyFlowNode[] {
  return useMemo(() => {
    if (nodes.length === 0) return [];

    const childrenMap = new Map<string, string[]>();
    const parentMap = new Map<string, string>();

    for (const edge of edges) {
      const kids = childrenMap.get(edge.source) ?? [];
      kids.push(edge.target);
      childrenMap.set(edge.source, kids);
      parentMap.set(edge.target, edge.source);
    }

    const nodeById = new Map(nodes.map(n => [n.id, n]));
    const roots = nodes.filter(n => !parentMap.has(n.id));

    const subtreeSize = new Map<string, number>();
    function calcSize(id: string): number {
      const kids = childrenMap.get(id) ?? [];
      if (kids.length === 0) {
        subtreeSize.set(id, 1);
        return 1;
      }
      const total = kids.reduce((acc, kid) => acc + calcSize(kid), 0);
      subtreeSize.set(id, total);
      return total;
    }
    for (const root of roots) calcSize(root.id);

    const positioned: OntologyFlowNode[] = [];
    const posMap = new Map<string, { x: number; y: number }>();

    function place(id: string, depth: number, yOffset: number): number {
      const node = nodeById.get(id);
      if (!node) return yOffset;

      const kids = childrenMap.get(id) ?? [];
      const isGene = node.type === 'gene';
      const gap = isGene ? Y_GAP_GENE : Y_GAP_DISEASE;

      if (kids.length === 0) {
        posMap.set(id, { x: depth * X_GAP, y: yOffset });
        positioned.push({ ...node, position: { x: depth * X_GAP, y: yOffset } });
        return yOffset + gap;
      }

      let cursor = yOffset;
      const kidStartY = cursor;
      for (const kidId of kids) {
        cursor = place(kidId, depth + 1, cursor);
      }
      const kidEndY = cursor - gap;
      const selfY = kidStartY + (kidEndY - kidStartY) / 2;

      posMap.set(id, { x: depth * X_GAP, y: selfY });
      positioned.push({ ...node, position: { x: depth * X_GAP, y: selfY } });
      return cursor;
    }

    let cursor = 0;
    for (const root of roots) {
      cursor = place(root.id, 0, cursor);
    }

    return positioned;
  }, [nodes, edges]);
}
